import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Sparkles, Lock, RotateCcw, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERM_LABELS = {
  dashboard: 'Dashboard', schedule: 'Schedule', jobs: 'Jobs', 'job-detail': 'Job Detail',
  customers: 'Customers', technicians: 'Technicians', equipment: 'Equipment', vendors: 'Vendors',
  reports: 'Reports (all)', 'daily-summary': 'Daily Summary', 'shop-time': 'Shop Time',
  settings: 'Settings', users: 'Users', 'form-templates': 'Forms',
  help: 'Help', billing: 'Billing', admin: 'Platform Admin',
  'safety-docs': 'Safety Docs Hub',
  'customers.edit': 'Customers · Edit',
  'vendors.edit': 'Vendors · Edit',
  'equipment.edit': 'Equipment · Edit',
  'form-templates.edit': 'Forms · Edit',
  'technicians.rates': 'Technicians · See Pay Rates',
  'reports.job': 'Reports · Job Cost',
  'reports.technician': 'Reports · By Technician',
  'reports.model': 'Reports · By Model',
  'reports.customer': 'Reports · By Customer',
  'reports.maintenance': 'Reports · Maintenance',
  'reports.compliance': 'Reports · Compliance',
  field: 'Field Division',
  'field.calendar': 'Field · Calendar View',
  'field.calendar.edit': 'Field · Calendar Edit',
  'field.assign': 'Field · Assign Techs',
  'field.schedule.manage': 'Field · Manage Schedules',
};

const NON_MGMT_ROLES = ['foreman', 'technician', 'admin_safety', 'field_tech', 'field_coordinator', 'field_management'];

export default function PermissionsSettings() {
  const [cfg, setCfg] = useState(null);
  const [savingRole, setSavingRole] = useState('');
  const [savingUser, setSavingUser] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/permissions/config`);
      setCfg(res.data);
      setSelectedUserId((prev) => {
        if (prev) return prev;
        const firstNonMgmt = res.data.users?.find((u) => u.role !== 'management');
        return firstNonMgmt ? firstNonMgmt.id : '';
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load permissions config');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!cfg) return null;

  const tierAllowsRoleOverrides = cfg.is_silver_or_above;
  const tierAllowsUserOverrides = cfg.is_gold_tier;
  const ungrantable = new Set(cfg.ungrantable_permissions || []);

  const saveRoleGrants = async (role, permissions) => {
    setSavingRole(role);
    try {
      await axios.put(`${API}/permissions/role/${role}`, { permissions });
      toast.success(`${role.charAt(0).toUpperCase() + role.slice(1)} permissions updated`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingRole('');
    }
  };

  const resetRole = async (role) => {
    if (!window.confirm(`Reset ${role} permissions to role default?`)) return;
    setSavingRole(role);
    try {
      await axios.post(`${API}/permissions/role/${role}/reset`);
      toast.success(`${role} overrides cleared`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setSavingRole('');
    }
  };

  const saveUserGrants = async (userId, permissions) => {
    setSavingUser(userId);
    try {
      await axios.put(`${API}/permissions/user/${userId}`, { permissions });
      toast.success('User permissions updated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingUser('');
    }
  };

  const resetUser = async (userId, userName) => {
    if (!window.confirm(`Reset all custom permissions for ${userName}?`)) return;
    setSavingUser(userId);
    try {
      await axios.post(`${API}/permissions/user/${userId}/reset`);
      toast.success(`${userName} permissions reset`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setSavingUser('');
    }
  };

  const toggleRolePerm = (role, perm) => {
    const current = new Set(cfg.role_grants[role] || []);
    if (current.has(perm)) current.delete(perm);
    else current.add(perm);
    saveRoleGrants(role, Array.from(current));
  };

  const toggleUserPerm = (userId, perm) => {
    const current = new Set(cfg.user_grants[userId] || []);
    if (current.has(perm)) current.delete(perm);
    else current.add(perm);
    saveUserGrants(userId, Array.from(current));
  };

  const selectedUser = cfg.users.find((u) => u.id === selectedUserId);
  const roleDefaultsForSelected = selectedUser ? new Set(cfg.role_defaults[selectedUser.role] || []) : new Set();
  const roleGrantsForSelected = selectedUser ? new Set(cfg.role_grants[selectedUser.role] || []) : new Set();

  return (
    <Card className="p-6 bg-white border border-slate-200 shadow-sm" data-testid="permissions-settings">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h4 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck size={20} className="text-slate-700" />
            Permissions
            {tierAllowsUserOverrides ? (
              <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full font-semibold flex items-center gap-1"><Sparkles size={12} /> Gold</span>
            ) : tierAllowsRoleOverrides ? (
              <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full font-semibold">Silver</span>
            ) : null}
          </h4>
          <p className="text-sm text-slate-500 mt-1">
            Grant-only overrides on top of each role&apos;s defaults. Removing a role default is not supported — untick a custom grant to revoke.
          </p>
        </div>
      </div>

      {!tierAllowsRoleOverrides && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 flex items-start gap-2" data-testid="tier-lock-notice">
          <Lock size={16} className="text-amber-700 mt-0.5" />
          <div className="text-sm text-amber-900">
            Permission overrides are a <strong>Silver tier</strong> feature. <a href="/billing" className="underline font-semibold">Upgrade your plan</a> to unlock role-level grants; add <strong>Gold</strong> for per-user grants.
          </div>
        </div>
      )}

      {tierAllowsRoleOverrides && (
        <>
          {/* Role-level grants */}
          <div className="space-y-5">
            {NON_MGMT_ROLES.map((role) => {
              const defaults = new Set(cfg.role_defaults[role] || []);
              const grants = new Set(cfg.role_grants[role] || []);
              return (
                <div key={role} className="border border-slate-200 rounded-md p-4" data-testid={`role-section-${role}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-bold uppercase tracking-wider text-slate-700">
                      {role.replace(/_/g, ' ')} — role grants
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => resetRole(role)}
                      disabled={savingRole === role || grants.size === 0}
                      data-testid={`reset-role-${role}`}
                    >
                      <RotateCcw size={12} className="mr-1" /> Reset
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {cfg.available_permissions.map((perm) => {
                      const isDefault = defaults.has(perm);
                      const isGranted = grants.has(perm);
                      const isUngrantable = ungrantable.has(perm);
                      const disabled = isDefault || isUngrantable || savingRole === role;
                      return (
                        <label
                          key={perm}
                          className={`flex items-center gap-2 rounded border px-3 py-2 text-sm ${
                            isDefault ? 'bg-slate-50 border-slate-200 text-slate-500'
                            : isUngrantable ? 'bg-red-50 border-red-100 text-red-700 opacity-70'
                            : isGranted ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
                            : 'border-slate-200 hover:border-slate-300 cursor-pointer'
                          } ${disabled && !isGranted && !isDefault ? 'opacity-60' : ''}`}
                          data-testid={`role-${role}-perm-${perm}`}
                        >
                          <Checkbox
                            checked={isDefault || isGranted}
                            disabled={disabled}
                            onCheckedChange={() => !disabled && toggleRolePerm(role, perm)}
                          />
                          <span className="flex-1">
                            {PERM_LABELS[perm] || perm}
                            {isDefault && <span className="ml-1 text-[10px] uppercase text-slate-400">·default</span>}
                            {isUngrantable && !isDefault && <span className="ml-1 text-[10px] uppercase text-red-500">·locked</span>}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Per-user grants (Gold) */}
          <div className="mt-8 pt-6 border-t border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
                  Per-user grants
                  {!tierAllowsUserOverrides && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-full font-semibold flex items-center gap-1">
                      <Sparkles size={10} /> Gold
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">Grant a specific user access beyond their role/role-grant defaults.</p>
              </div>
            </div>

            {tierAllowsUserOverrides ? (
              <>
                <div className="flex items-center gap-2 mb-4">
                  <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                    <SelectTrigger className="max-w-xs" data-testid="user-select"><SelectValue placeholder="Choose a user" /></SelectTrigger>
                    <SelectContent>
                      {cfg.users.filter((u) => u.role !== 'management').map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name} ({u.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedUser && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => resetUser(selectedUser.id, selectedUser.name)}
                      disabled={savingUser === selectedUser.id || !(cfg.user_grants[selectedUser.id] || []).length}
                      data-testid="reset-user-button"
                    >
                      <RotateCcw size={12} className="mr-1" /> Reset
                    </Button>
                  )}
                </div>

                {selectedUser ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="user-perm-grid">
                    {cfg.available_permissions.map((perm) => {
                      const grantedByDefault = roleDefaultsForSelected.has(perm);
                      const grantedByRole = roleGrantsForSelected.has(perm);
                      const grantedByUser = (cfg.user_grants[selectedUser.id] || []).includes(perm);
                      const isUngrantable = ungrantable.has(perm);
                      const disabled = grantedByDefault || grantedByRole || isUngrantable || savingUser === selectedUser.id;
                      const checked = grantedByDefault || grantedByRole || grantedByUser;
                      const source = grantedByDefault ? 'default'
                        : grantedByRole ? 'role'
                        : grantedByUser ? 'user'
                        : null;
                      return (
                        <label
                          key={perm}
                          className={`flex items-center gap-2 rounded border px-3 py-2 text-sm ${
                            grantedByDefault ? 'bg-slate-50 border-slate-200 text-slate-500'
                            : grantedByRole ? 'bg-slate-50 border-slate-200 text-slate-600'
                            : isUngrantable ? 'bg-red-50 border-red-100 text-red-700 opacity-70'
                            : grantedByUser ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
                            : 'border-slate-200 hover:border-slate-300 cursor-pointer'
                          }`}
                          data-testid={`user-perm-${perm}`}
                        >
                          <Checkbox
                            checked={checked}
                            disabled={disabled}
                            onCheckedChange={() => !disabled && toggleUserPerm(selectedUser.id, perm)}
                          />
                          <span className="flex-1">
                            {PERM_LABELS[perm] || perm}
                            {source && <span className="ml-1 text-[10px] uppercase text-slate-400">·{source}</span>}
                            {isUngrantable && !source && <span className="ml-1 text-[10px] uppercase text-red-500">·locked</span>}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 italic">No non-management users in this org. Invite one first from Users.</p>
                )}
              </>
            ) : (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-4 flex items-start gap-2" data-testid="gold-lock-notice">
                <Lock size={16} className="text-amber-700 mt-0.5" />
                <div className="text-sm text-amber-900">
                  Per-user overrides require <strong>Gold tier</strong>. <a href="/billing" className="underline font-semibold">Upgrade</a> to unlock.
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </Card>
  );
}
